import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const DEPARTMENTS = [
  "admin",
  "editor",
  "graphic_designer",
  "ai",
  "marketer",
  "seo",
  "smm",
  "scriptwriter",
  "calling",
  "hr",
] as const;

const sendNotificationSchema = z
  .object({
    heading: z.string().trim().min(3, "Heading must be at least 3 characters").max(80),
    description: z.string().trim().min(10, "Description must be at least 10 characters").max(500),
    send_type: z.enum(["instant", "scheduled", "draft"]),
    scheduled_at: z.string().datetime().nullable().optional(),
    target_type: z.enum(["all", "department", "individual"]),
    target_dept: z.array(z.enum(DEPARTMENTS)).nullable().optional(),
    target_user_id: z.string().uuid().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.send_type === "scheduled") {
      if (!v.scheduled_at) {
        ctx.addIssue({ code: "custom", message: "scheduled_at is required", path: ["scheduled_at"] });
      } else if (new Date(v.scheduled_at).getTime() <= Date.now()) {
        ctx.addIssue({ code: "custom", message: "scheduled_at must be in the future", path: ["scheduled_at"] });
      }
    }
    if (v.target_type === "department" && (!v.target_dept || v.target_dept.length === 0)) {
      ctx.addIssue({ code: "custom", message: "Pick at least one department", path: ["target_dept"] });
    }
    if (v.target_type === "individual" && !v.target_user_id) {
      ctx.addIssue({ code: "custom", message: "Pick a user", path: ["target_user_id"] });
    }
  });

async function isAdmin(userId: string) {
  const { data } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  return (data ?? []).some((r) => r.role === "admin");
}

async function assertCanSend(userId: string) {
  const { data: profile } = await supabaseAdmin
    .from("profiles")
    .select("department, moderator_permissions")
    .eq("id", userId)
    .maybeSingle();
  const { data: roles } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  const roleSet = new Set((roles ?? []).map((r) => r.role));

  if (roleSet.has("admin")) return;
  if (profile?.department === "hr") return;
  if (roleSet.has("moderator")) {
    const perms = (profile?.moderator_permissions ?? {}) as Record<string, unknown>;
    if (perms.send_notifications === true) return;
  }
  throw new Error("Not authorized to send notifications");
}

function audienceCount(
  n: { target_type: string; target_dept: string[] | null; target_user_id: string | null },
  profiles: { id: string; department: string }[],
) {
  if (n.target_type === "all") return profiles.length;
  if (n.target_type === "department") {
    const set = new Set(n.target_dept ?? []);
    return profiles.filter((p) => set.has(p.department)).length;
  }
  if (n.target_type === "individual") return n.target_user_id ? 1 : 0;
  return 0;
}

export const sendPushNotification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => sendNotificationSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertCanSend(context.userId);

    const isInstant = data.send_type === "instant";
    const isDraft = data.send_type === "draft";

    const row = {
      created_by: context.userId,
      heading: data.heading,
      description: data.description,
      send_type: isDraft ? "instant" : data.send_type,
      scheduled_at: data.send_type === "scheduled" ? data.scheduled_at : null,
      target_type: data.target_type,
      target_dept: data.target_type === "department" ? (data.target_dept ?? null) : null,
      target_user_id: data.target_type === "individual" ? (data.target_user_id ?? null) : null,
      status: isDraft ? "draft" : isInstant ? "sent" : "pending",
      sent_at: isInstant ? new Date().toISOString() : null,
    };

    const { data: inserted, error } = await supabaseAdmin
      .from("push_notifications")
      .insert(row)
      .select()
      .single();
    if (error) throw new Error(error.message);

    return { id: inserted.id, status: inserted.status };
  });

export const listNotificationHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const admin = await isAdmin(context.userId);

    let q = supabaseAdmin
      .from("push_notifications")
      .select("*")
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .limit(200);
    if (!admin) q = q.eq("created_by", context.userId);

    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    const ids = (rows ?? []).map((r) => r.id);
    const { data: reads } = ids.length
      ? await supabaseAdmin
          .from("push_notification_reads")
          .select("notification_id")
          .in("notification_id", ids)
      : { data: [] as { notification_id: string }[] };
    const readCounts = new Map<string, number>();
    for (const r of reads ?? []) {
      readCounts.set(r.notification_id, (readCounts.get(r.notification_id) ?? 0) + 1);
    }

    const { data: profiles } = await supabaseAdmin
      .from("profiles")
      .select("id, department, full_name");
    const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));

    return (rows ?? []).map((r) => ({
      ...r,
      read_count: readCounts.get(r.id) ?? 0,
      audience_total: audienceCount(
        {
          target_type: r.target_type,
          target_dept: (r.target_dept as string[] | null) ?? null,
          target_user_id: r.target_user_id,
        },
        (profiles ?? []).map((p) => ({ id: p.id, department: p.department as string })),
      ),
      created_by_name: profileMap.get(r.created_by)?.full_name ?? null,
      target_user_name: r.target_user_id
        ? profileMap.get(r.target_user_id)?.full_name ?? null
        : null,
    }));
  });

export const deletePushNotification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const admin = await isAdmin(context.userId);
    let q = supabaseAdmin
      .from("push_notifications")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", data.id);
    if (!admin) q = q.eq("created_by", context.userId);
    const { error } = await q;
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const markPushNotificationRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await supabaseAdmin
      .from("push_notification_reads")
      .upsert(
        { notification_id: data.id, user_id: context.userId },
        { onConflict: "notification_id,user_id", ignoreDuplicates: true },
      );
    if (error && !/duplicate/i.test(error.message)) throw new Error(error.message);
    return { ok: true };
  });
